import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

/**
 * iter 91 - Color overhaul for /portal/reset-password.
 *
 * Bug: input text and the password strength indicator were rendering
 * white-on-white (the form card was a leftover dark-theme `bg-[#111]` block
 * sitting on the cream page background, with `text-white` inputs on
 * `bg-white` fields). Both were invisible.
 *
 * Fix: input text now uses Deep Ocean `#0A1F2E`; the strength meter uses
 * teal/dark colors per Mundalo brand. Surrounding card updated to the cream
 * Mundalo aesthetic so the new colors actually contrast correctly.
 */

/**
 * Score a password 0–4 based on simple heuristics:
 *   • length ≥ 8         (required by the API anyway)
 *   • length ≥ 12
 *   • mixed case
 *   • includes a digit
 *   • includes a symbol
 * Returns { score, label, barClass, textClass } so the indicator stays
 * fully self-contained.
 */
const scorePassword = (pw) => {
  if (!pw) return { score: 0, label: '', barClass: '', textClass: '', widthClass: 'w-0' };
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  s = Math.min(s, 4);

  // Map score → presentation. Always teal-on-cream / Deep Ocean text - no
  // light-grey-on-white anywhere.
  const map = [
    { label: 'Too short',  barClass: 'bg-[#C97A4A]', textClass: 'text-[#C97A4A]', widthClass: 'w-1/4' },
    { label: 'Weak',       barClass: 'bg-[#C97A4A]', textClass: 'text-[#C97A4A]', widthClass: 'w-1/4' },
    { label: 'Fair',       barClass: 'bg-[#E8A830]', textClass: 'text-[#1A5C6E]', widthClass: 'w-2/4' },
    { label: 'Good',       barClass: 'bg-[#2CA8A0]', textClass: 'text-[#1A5C6E]', widthClass: 'w-3/4' },
    { label: 'Strong',     barClass: 'bg-[#1A5C6E]', textClass: 'text-[#0A1F2E]', widthClass: 'w-full' },
  ];
  return { score: s, ...map[s] };
};

const PortalResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const strength = useMemo(() => scorePassword(password), [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, password, confirmPassword);
      setSuccess(true);
      setTimeout(() => navigate('/portal/dashboard'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F9F6EF]">
        <Header currentPage="portal" />
        <section className="pt-32 pb-20">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white border border-[#EFE6CF] rounded-2xl shadow-sm p-8 text-center">
              <AlertCircle className="w-16 h-16 text-[#C97A4A] mx-auto mb-4" />
              <h1 className="font-display italic text-2xl text-[#0A1F2E] mb-2">Invalid Reset Link</h1>
              <p className="text-[#0A1F2E]/65 text-sm mb-6">This password reset link is invalid or has expired.</p>
              <button
                onClick={() => navigate('/portal')}
                className="bg-[#0A1F2E] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#163448] transition"
                data-testid="reset-back-to-signin"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <Header currentPage="portal" />

      <section className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border border-[#EFE6CF] rounded-2xl shadow-sm p-8" data-testid="reset-password-card">
            {/* Logo */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#2CA8A0] flex items-center justify-center mx-auto mb-4 rounded-xl">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <h1 className="font-display italic text-3xl text-[#0A1F2E]">Set New Password</h1>
              <p className="text-[#0A1F2E]/60 text-sm mt-2">Pick something memorable. We'll only see the hash.</p>
            </div>

            {success ? (
              <div className="text-center" data-testid="reset-success">
                <CheckCircle className="w-16 h-16 text-[#2CA8A0] mx-auto mb-4" />
                <p className="text-[#0A1F2E] font-medium mb-2">Password reset successful!</p>
                <p className="text-[#0A1F2E]/60 text-sm">Redirecting to your portal...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div
                    className="p-3 bg-[#C97A4A]/10 border border-[#C97A4A]/40 text-[#8E4A1F] text-sm flex items-center gap-2 rounded-lg"
                    data-testid="reset-error"
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="reset-password" className="block text-sm font-medium text-[#0A1F2E] mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A5C6E]" />
                    <input
                      id="reset-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] placeholder:text-[#0A1F2E]/40 pl-12 pr-12 py-3 rounded-lg focus:border-[#2CA8A0] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/25 transition"
                      placeholder="Min 8 characters"
                      data-testid="reset-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A5C6E] hover:text-[#0A1F2E]"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Password strength indicator - visible teal/dark on cream */}
                  {password && (
                    <div className="mt-2.5" data-testid="password-strength">
                      <div className="h-1.5 w-full bg-[#EFE6CF] rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${strength.barClass} ${strength.widthClass}`}
                          data-testid="password-strength-bar"
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span
                          className={`text-xs font-medium ${strength.textClass}`}
                          data-testid="password-strength-label"
                        >
                          {strength.label}
                        </span>
                        <span className="text-[11px] text-[#0A1F2E]/55 font-mono">
                          {password.length} chars
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="reset-confirm" className="block text-sm font-medium text-[#0A1F2E] mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A5C6E]" />
                    <input
                      id="reset-confirm"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] placeholder:text-[#0A1F2E]/40 pl-12 pr-4 py-3 rounded-lg focus:border-[#2CA8A0] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/25 transition"
                      placeholder="Repeat the password"
                      data-testid="reset-confirm"
                    />
                  </div>
                  {confirmPassword && password && confirmPassword !== password && (
                    <p className="text-xs text-[#C97A4A] mt-1.5" data-testid="reset-confirm-mismatch">
                      Passwords don't match yet.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0A1F2E] text-white py-3 rounded-full font-semibold hover:bg-[#163448] transition disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="reset-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortalResetPassword;
